import * as signalR from "@microsoft/signalr";

class SignalRService {
  constructor() {
    this.connection = null;
    this.isConnected = false;
    this.listeners = new Map();
    this.currentUserId = null;
    this._connectingPromise = null;
  }

  async startConnection(userId) {
    if (!userId) return;

    if (this.connection && this.isConnected && this.currentUserId === userId) {
      return;
    }

    // Return the in-flight promise instead of starting a second connection
    if (this._connectingPromise) {
      return this._connectingPromise;
    }

    if (this.connection && this.isConnected && this.currentUserId !== userId) {
      await this.stopConnection();
    }

    this._connectingPromise = this._connect(userId);
    try {
      await this._connectingPromise;
    } finally {
      this._connectingPromise = null;
    }
  }

  async _connect(userId) {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}`;
    const hasToken = !!localStorage.getItem("token");
    const userQuery = !hasToken ? `?userId=${encodeURIComponent(userId)}` : "";

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(`${apiUrl}/hubs/notification${userQuery}`, {
        accessTokenFactory: () => localStorage.getItem("token") || "",
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Information)
      .build();

    this.connection.onclose(() => {
      this.isConnected = false;
    });

    this.connection.onreconnecting(() => {
      this.isConnected = false;
    });

    this.connection.onreconnected(() => {
      this.isConnected = true;
    });

    try {
      await this.connection.start();
      this.isConnected = true;
      this.currentUserId = userId;

      this.connection.on("ReceiveNotification", (notification) => {
        this.notifyListeners("newNotification", notification);
      });

      this.connection.on("NotificationRead", (data) => {
        this.notifyListeners("notificationRead", data);
      });

      this.connection.on("AllNotificationsRead", (data) => {
        this.notifyListeners("allNotificationsRead", data);
      });
    } catch (error) {
      console.error("SignalR Connection Error:", error);
      this.connection = null;
      this.isConnected = false;
      this.currentUserId = null;
    }
  }

  async stopConnection() {
    if (this.connection) {
      try {
        await this.connection.stop();
      } catch (error) {
        console.error("Error stopping SignalR connection:", error);
      } finally {
        this.connection = null;
        this.isConnected = false;
        this.currentUserId = null;
      }
    }
  }

  addListener(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    const callbacks = this.listeners.get(event);
    if (!callbacks.includes(callback)) {
      callbacks.push(callback);
    }
  }

  removeListener(event, callback) {
    if (this.listeners.has(event)) {
      this.listeners.set(
        event,
        this.listeners.get(event).filter((cb) => cb !== callback)
      );
    }
  }

  notifyListeners(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach((callback) => callback(data));
    }
  }
}

export default new SignalRService();
